import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist/legacy/build/pdf.mjs";
import {
  ANNOTATION_COLOR_HEX,
  ANNOTATION_COLORS,
  type AnnotationColor,
  type AnnotationDto,
  type AnnotationShape,
  type DocumentDto,
  type DocumentVersionDto,
} from "@klantpunt/shared";
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconCloud,
  IconDownload,
  IconHand,
  IconList,
  IconPen,
  IconPolyline,
  IconCheck,
  IconText,
  IconTrash,
  IconUndo,
  IconZoomIn,
  IconZoomOut,
} from "../../app/icons";
import { api } from "../../lib/api";
import { loadPlanPdf, pxScale } from "../../lib/pdf";
import { useToast } from "../../lib/toast";
import { errMsg } from "./DossierDialogs";
import { cloudPoints, fontOf, pdfWithMarkup, strokeOf, svgPath, textBox } from "./markup";
import { fileUrl, fmtDate } from "./useDossier";

type Tool = "view" | "pen" | "line" | "cloud" | "text";
type Item = Pick<AnnotationDto, "id" | "page" | "color" | "shape"> & Partial<AnnotationDto>;

const ZOOMS = [1, 1.5, 2, 3, 4];
const TOOLS: { key: Tool; label: string; Icon: typeof IconPen }[] = [
  { key: "view", label: "Bekijken", Icon: IconHand },
  { key: "pen", label: "Pen", Icon: IconPen },
  { key: "line", label: "Lijnen", Icon: IconPolyline },
  { key: "cloud", label: "Wolk", Icon: IconCloud },
  { key: "text", label: "Tekst", Icon: IconText },
];
const KIND_LABEL: Record<AnnotationShape["kind"], string> = {
  pen: "Pen",
  line: "Lijn",
  cloud: "Wolk",
  text: "Tekst",
};
const kindLabel = (s: AnnotationShape) => (s.kind === "line" && s.closed ? "Vlak" : KIND_LABEL[s.kind]);
const COLOR_LABEL: Record<AnnotationColor, string> = { rood: "Rood", blauw: "Blauw", groen: "Groen" };

export const annotationsKey = (docId: string, n: number) => ["annotations", docId, n] as const;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Markeren (docs/DOSSIER.md): wijzigingen intekenen op een PDF (pen, revisiewolk, tekst) zonder een nieuwe
 * revisie te maken. Markeringen horen bij één versie; downloaden kan met de markeringen erin.
 */
export function MarkupViewer({
  doc,
  version,
  canEdit,
  onClose,
}: {
  doc: DocumentDto;
  version: DocumentVersionDto;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const base = `/documents/${doc.id}/versions/${version.version}/annotations`;
  const q = useQuery({ queryKey: annotationsKey(doc.id, version.version), queryFn: () => api<AnnotationDto[]>(base) });
  const [items, setItems] = useState<Item[]>([]);
  useEffect(() => {
    if (q.data) setItems(q.data);
  }, [q.data]);

  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [avail, setAvail] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(0);
  const [tool, setTool] = useState<Tool>(canEdit ? "pen" : "view");
  const [color, setColor] = useState<AnnotationColor>("rood");
  const [draft, setDraft] = useState<AnnotationShape | null>(null);
  /** Lijnen: geplaatste punten en waar de aanwijzer nu is (voor het volgende stuk). */
  const [linePts, setLinePts] = useState<[number, number][]>([]);
  const [hover, setHover] = useState<[number, number] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [textAt, setTextAt] = useState<{ x: number; y: number } | null>(null);
  const [text, setText] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const undo = useRef<string[]>([]);
  const body = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const start = useRef<[number, number] | null>(null);

  // Focus in de viewer, en terug waar je was bij sluiten.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    return () => prev?.focus?.();
  }, []);

  useEffect(() => {
    let alive = true;
    loadPlanPdf(version.fileId).then(
      (p) => alive && setPdf(p),
      () => alive && setLoadError("Deze PDF kan niet worden geopend"),
    );
    return () => {
      alive = false;
    };
  }, [version.fileId]);

  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setAvail({ w: el.clientWidth - 24, h: el.clientHeight - 24 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Blad tekenen op de gekozen grootte.
  const cssW = size ? Math.max(200, Math.min(avail.w, (avail.h * size.w) / size.h) * ZOOMS[zoom]!) : 0;
  const cssH = size ? (cssW * size.h) / size.w : 0;
  useEffect(() => {
    if (!pdf) return;
    let task: RenderTask | null = null;
    let alive = true;
    void pdf.getPage(pageNo).then((page) => {
      if (!alive) return;
      const vp1 = page.getViewport({ scale: 1 });
      setSize((s) => (s && s.w === vp1.width && s.h === vp1.height ? s : { w: vp1.width, h: vp1.height }));
      const c = canvas.current;
      if (!c || !cssW) return;
      const scale = (cssW / vp1.width) * pxScale(cssW, cssH, window.devicePixelRatio || 1);
      const vp = page.getViewport({ scale });
      c.width = Math.round(vp.width);
      c.height = Math.round(vp.height);
      task = page.render({ canvas: c, viewport: vp });
      task.promise.catch(() => undefined);
    });
    return () => {
      alive = false;
      task?.cancel();
    };
  }, [pdf, pageNo, cssW, cssH]);

  const W = size?.w ?? 1;
  const H = size?.h ?? 1;
  const pageItems = items.filter((a) => a.page === pageNo);
  const drawing = canEdit && tool !== "view";

  const save = async (shape: AnnotationShape) => {
    const a: Item = { id: crypto.randomUUID(), page: pageNo, color, shape };
    setItems((l) => [...l, a]);
    undo.current.push(a.id);
    try {
      await api(`${base}/${a.id}`, { method: "PUT", body: { page: a.page, color: a.color, shape: a.shape } });
    } catch (err) {
      setItems((l) => l.filter((x) => x.id !== a.id));
      toast(errMsg(err));
    }
  };
  const remove = async (id: string) => {
    const old = items;
    setItems((l) => l.filter((x) => x.id !== id));
    setSel(null);
    undo.current = undo.current.filter((x) => x !== id);
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
    } catch (err) {
      setItems(old);
      toast(errMsg(err));
    }
  };

  const finishLine = (closed: boolean) => {
    if (linePts.length >= (closed ? 3 : 2)) void save({ kind: "line", points: linePts, closed });
    setLinePts([]);
    setHover(null);
  };
  const cancelLine = () => {
    setLinePts([]);
    setHover(null);
  };
  /** Afstand in schermpixels tussen twee genormaliseerde punten. */
  const px = (a: [number, number], b: [number, number]) => Math.hypot((a[0] - b[0]) * cssW, (a[1] - b[1]) * cssH);

  const at = (e: RPointerEvent): [number, number] => {
    const r = svg.current!.getBoundingClientRect();
    return [clamp01((e.clientX - r.left) / r.width), clamp01((e.clientY - r.top) / r.height)];
  };
  const onDown = (e: RPointerEvent<SVGSVGElement>) => {
    if (!drawing || textAt) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = at(e);
    start.current = p;
    if (tool === "pen") setDraft({ kind: "pen", points: [p, p] });
    if (tool === "cloud") setDraft({ kind: "cloud", x: p[0], y: p[1], w: 0, h: 0 });
  };
  const onMove = (e: RPointerEvent<SVGSVGElement>) => {
    if (tool === "line" && linePts.length) setHover(at(e));
    if (!start.current || !draft) return;
    const p = at(e);
    if (draft.kind === "pen") {
      const last = draft.points[draft.points.length - 1]!;
      if (Math.hypot(p[0] - last[0], p[1] - last[1]) < 0.0015 || draft.points.length >= 4000) return;
      setDraft({ ...draft, points: [...draft.points, p] });
    } else if (draft.kind === "cloud") {
      const [sx, sy] = start.current;
      setDraft({
        kind: "cloud",
        x: Math.min(sx, p[0]),
        y: Math.min(sy, p[1]),
        w: Math.abs(p[0] - sx),
        h: Math.abs(p[1] - sy),
      });
    }
  };
  const onUp = (e: RPointerEvent<SVGSVGElement>) => {
    if (!drawing || !start.current) return;
    const p = at(e);
    start.current = null;
    const d = draft;
    setDraft(null);
    if (tool === "line") {
      const first = linePts[0];
      const last = linePts[linePts.length - 1];
      // Tik op het eerste punt sluit het vlak; nog eens op het laatste punt maakt de lijn af.
      if (first && linePts.length >= 3 && px(p, first) < 14) finishLine(true);
      else if (last && px(p, last) < 10) finishLine(false);
      else if (linePts.length < 200) setLinePts((l) => [...l, p]);
      return;
    }
    if (tool === "text") {
      setText("");
      setTextAt({ x: p[0], y: p[1] });
      return;
    }
    if (d?.kind === "pen" && d.points.length > 2) void save(d);
    if (d?.kind === "cloud" && d.w > 0.01 && d.h > 0.01) void save(d);
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(fileUrl(version.fileId), { credentials: "same-origin" });
      if (!res.ok) throw new Error("Downloaden mislukt");
      const today = new Date().toLocaleDateString("nl-NL");
      const blob = await pdfWithMarkup(
        await res.arrayBuffer(),
        items,
        `Markeringen ${today} bij versie ${version.version}${doc.revision ? ` (rev. ${doc.revision})` : ""}, nog geen nieuwe revisie`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title} (gemarkeerd).pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Downloaden mislukt");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    void qc.invalidateQueries({ queryKey: ["document", doc.id] });
    void qc.invalidateQueries({ queryKey: ["dossier", doc.buildingId] });
    void qc.invalidateQueries({ queryKey: ["logbook", "document", doc.id] });
    onClose();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (textAt) setTextAt(null);
      else if (linePts.length) cancelLine();
      else if (sel) setSel(null);
      else if (listOpen) setListOpen(false);
      else close();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  const n = pdf?.numPages ?? 1;
  const sw = strokeOf(W, H);
  const fs = fontOf(W, H);
  const shapeEl = (a: Pick<Item, "color" | "shape">, key: string, onPick?: () => void, selected = false) => {
    const hex = ANNOTATION_COLOR_HEX[a.color];
    const s = a.shape;
    const pick = onPick
      ? {
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onPick();
          },
          className: "mk-pick",
        }
      : {};
    if (s.kind === "text") {
      const box = textBox(s.text, fs);
      const x = s.x * W;
      const y = s.y * H;
      return (
        <g key={key} {...pick} data-selected={selected || undefined}>
          <rect
            x={x}
            y={y}
            width={box.w}
            height={box.h}
            fill="#fff"
            fillOpacity={0.9}
            stroke={hex}
            strokeWidth={sw * 0.6}
          />
          {box.lines.map((l, i) => (
            <text key={i} x={x + box.pad} y={y + box.pad + (i + 1) * fs * 1.25 - fs * 0.3} fontSize={fs} fill={hex}>
              {l}
            </text>
          ))}
        </g>
      );
    }
    const closed = s.kind === "line" && s.closed;
    const d =
      s.kind === "pen" || s.kind === "line"
        ? `${svgPath(s.points.map(([x, y]) => [x * W, y * H]))}${closed ? " Z" : ""}`
        : `${svgPath(cloudPoints(s.x * W, s.y * H, s.w * W, s.h * H, { w: W, h: H }))} Z`;
    return (
      <g key={key} {...pick} data-selected={selected || undefined}>
        {onPick && <path d={d} stroke="transparent" strokeWidth={sw * 8} fill={closed ? "transparent" : "none"} />}
        <path
          d={d}
          stroke={hex}
          strokeWidth={sw}
          fill={closed ? hex : "none"}
          fillOpacity={closed ? 0.15 : undefined}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  };

  return (
    <div className="mk" role="dialog" aria-modal="true" aria-label={`Markeren: ${doc.title}`}>
      <header className="mk-head">
        <button
          ref={closeBtn}
          type="button"
          className="btn ghost small ico"
          onClick={close}
          aria-label="Markeren sluiten"
        >
          <IconClose aria-hidden="true" />
        </button>
        <div className="grow mk-title">
          <strong>{doc.title}</strong>
          <span className="muted small">
            Versie {version.version}
            {version.revision && ` · rev. ${version.revision}`} · {items.length}{" "}
            {items.length === 1 ? "markering" : "markeringen"}
          </span>
        </div>
        <button
          type="button"
          className="btn small ghost"
          aria-pressed={listOpen}
          onClick={() => setListOpen((v) => !v)}
          aria-label={`Lijst met markeringen (${items.length})`}
        >
          <IconList size={18} aria-hidden="true" />
          <span className="mk-label">Lijst</span>
        </button>
        <button type="button" className="btn small" onClick={() => void download()} disabled={busy || !pdf}>
          <IconDownload size={18} aria-hidden="true" />
          <span className="mk-label">Downloaden met markeringen</span>
        </button>
      </header>
      <div className="mk-main">
        <div className={`mk-body${drawing ? " drawing" : ""}`} ref={body} onClick={() => setSel(null)}>
          {loadError ? (
            <p className="alert" role="alert">
              {loadError}
            </p>
          ) : !size ? (
            <p className="muted" role="status">
              Laden…
            </p>
          ) : null}
          <div className="mk-page" style={{ width: cssW, height: cssH, display: size ? undefined : "none" }}>
            <canvas ref={canvas} style={{ width: cssW, height: cssH }} aria-hidden="true" />
            <svg
              ref={svg}
              viewBox={`0 0 ${W} ${H}`}
              width={cssW}
              height={cssH}
              role="img"
              aria-label={`Blad ${pageNo} met ${pageItems.length} ${pageItems.length === 1 ? "markering" : "markeringen"}`}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => {
                start.current = null;
                setDraft(null);
              }}
            >
              {pageItems.map((a) =>
                shapeEl(a, a.id, canEdit && tool === "view" ? () => setSel(a.id) : undefined, sel === a.id),
              )}
              {draft && shapeEl({ color, shape: draft }, "draft")}
              {linePts.length > 0 && (
                <g className="mk-linedraft">
                  {shapeEl(
                    {
                      color,
                      shape: {
                        kind: "line",
                        points: hover ? [...linePts, hover] : [...linePts, linePts[0]!],
                        closed: false,
                      },
                    },
                    "line-draft",
                  )}
                  {linePts.map(([x, y], i) => (
                    <circle
                      key={i}
                      cx={x * W}
                      cy={y * H}
                      r={sw * (i === 0 && linePts.length >= 3 ? 4 : 2.5)}
                      fill="#fff"
                      stroke={ANNOTATION_COLOR_HEX[color]}
                      strokeWidth={sw * 0.8}
                    />
                  ))}
                </g>
              )}
            </svg>
          </div>
        </div>
        {listOpen && (
          <aside className="mk-list" aria-label="Markeringen">
            <h3>Markeringen</h3>
            {items.length === 0 ? (
              <p className="muted small">Nog geen markeringen op deze versie.</p>
            ) : (
              <ul className="dir-items">
                {[...items]
                  .sort((a, b) => a.page - b.page)
                  .map((a) => (
                    <li key={a.id} aria-current={sel === a.id}>
                      <button
                        type="button"
                        className="linkbtn grow"
                        onClick={() => {
                          setPageNo(a.page);
                          setTool("view");
                          setSel(a.id);
                        }}
                      >
                        <span className="mk-swatch" style={{ background: ANNOTATION_COLOR_HEX[a.color] }} />
                        {a.shape.kind === "text" ? a.shape.text : kindLabel(a.shape)}
                        <span className="muted small block">
                          Blad {a.page}
                          {a.createdBy && ` · ${a.createdBy}`}
                          {a.createdAt && ` · ${fmtDate(a.createdAt)}`}
                        </span>
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          className="btn ghost ico small"
                          aria-label="Markering verwijderen"
                          onClick={() => void remove(a.id)}
                        >
                          <IconTrash size={18} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </aside>
        )}
      </div>
      {textAt && (
        <form
          className="mk-textform"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            void save({ kind: "text", x: textAt.x, y: textAt.y, text: text.trim() });
            setTextAt(null);
          }}
        >
          <label className="field">
            <span className="label">Tekst op de tekening</span>
            <textarea
              rows={2}
              maxLength={500}
              value={text}
              autoFocus
              onChange={(e) => setText(e.target.value)}
              placeholder="bijv. Klep verplaatst naar de gang"
            />
          </label>
          <div className="row">
            <span className="grow" />
            <button type="button" className="btn ghost small" onClick={() => setTextAt(null)}>
              Annuleren
            </button>
            <button type="submit" className="btn primary small" disabled={!text.trim()}>
              Plaatsen
            </button>
          </div>
        </form>
      )}
      {canEdit && tool === "line" && (
        <p className="mk-hint small" role="status">
          {linePts.length === 0
            ? "Tik de punten van de lijn aan."
            : linePts.length < 3
              ? "Tik het volgende punt aan, of kies Klaar."
              : "Tik op het eerste punt om een vlak te sluiten, of kies Klaar."}
        </p>
      )}
      <footer className="mk-foot">
        {canEdit && (
          <>
            <div className="mk-group" role="group" aria-label="Gereedschap">
              {TOOLS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  className="btn small ghost"
                  aria-pressed={tool === key}
                  onClick={() => {
                    setTool(key);
                    setSel(null);
                    cancelLine();
                  }}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span className="mk-label">{label}</span>
                </button>
              ))}
            </div>
            <div className="mk-group" role="group" aria-label="Kleur">
              {ANNOTATION_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="btn small ghost ico mk-color"
                  aria-pressed={color === c}
                  aria-label={COLOR_LABEL[c]}
                  onClick={() => setColor(c)}
                >
                  <span className="mk-swatch" style={{ background: ANNOTATION_COLOR_HEX[c] }} />
                </button>
              ))}
            </div>
            {linePts.length > 0 ? (
              <div className="mk-group" role="group" aria-label="Lijn afmaken">
                <button
                  type="button"
                  className="btn small primary"
                  disabled={linePts.length < 2}
                  onClick={() => finishLine(false)}
                >
                  <IconCheck size={18} aria-hidden="true" /> Klaar
                </button>
                <button
                  type="button"
                  className="btn small"
                  disabled={linePts.length < 3}
                  onClick={() => finishLine(true)}
                >
                  Vlak sluiten
                </button>
                <button type="button" className="btn small ghost" onClick={cancelLine}>
                  Annuleren
                </button>
              </div>
            ) : sel ? (
              <button type="button" className="btn small danger" onClick={() => void remove(sel)}>
                <IconTrash size={18} aria-hidden="true" /> Verwijderen
              </button>
            ) : (
              <button
                type="button"
                className="btn small ghost"
                disabled={!undo.current.length}
                onClick={() => {
                  const id = undo.current[undo.current.length - 1];
                  if (id) void remove(id);
                }}
              >
                <IconUndo size={18} aria-hidden="true" />
                <span className="mk-label">Ongedaan maken</span>
              </button>
            )}
          </>
        )}
        <span className="grow" />
        <div className="mk-group" role="group" aria-label="Zoomen">
          <button
            type="button"
            className="btn small ghost ico"
            aria-label="Uitzoomen"
            disabled={zoom === 0}
            onClick={() => setZoom((z) => Math.max(0, z - 1))}
          >
            <IconZoomOut size={18} aria-hidden="true" />
          </button>
          <span className="small num mk-zoom">{Math.round(ZOOMS[zoom]! * 100)}%</span>
          <button
            type="button"
            className="btn small ghost ico"
            aria-label="Inzoomen"
            disabled={zoom === ZOOMS.length - 1}
            onClick={() => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1))}
          >
            <IconZoomIn size={18} aria-hidden="true" />
          </button>
        </div>
        {n > 1 && (
          <div className="mk-group" role="group" aria-label="Bladen">
            <button
              type="button"
              className="btn small ghost ico"
              aria-label="Vorig blad"
              disabled={pageNo === 1}
              onClick={() => setPageNo((p) => p - 1)}
            >
              <IconChevronLeft size={18} aria-hidden="true" />
            </button>
            <span className="small num" aria-live="polite">
              Blad {pageNo} van {n}
            </span>
            <button
              type="button"
              className="btn small ghost ico"
              aria-label="Volgend blad"
              disabled={pageNo === n}
              onClick={() => setPageNo((p) => p + 1)}
            >
              <IconChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        )}
      </footer>
    </div>
  );
}
